import type { Timestamp } from "firebase/firestore";

export interface Contact {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string;
  company: string;
  company2: string;
  companyOthers: string;
  title: string;
  title2: string;
  titleOthers: string;
  industry: string;
  location: string;
  sources: string[];
  emails: string[];
  mobiles: string[];
  telephones: string[];
  linkedinUrl: string;
  linkedinConnectedOn: string;
  camcardCreateTime: string;
  cardImageUrl: string;
  cardImageBackUrl: string;
  cardImagePath: string;
  cardImageBackPath: string;
  cardImagePaths: string[];
  cardImageBackPaths: string[];
  photoUrl: string;
  chineseName: string;
  notes: string;
  confidentiality: string;
  relationshipStrength: number;
  followUpFlag: boolean;
  followUpDate: string;
  followUpNote: string;
  lastFollowedUpAt?: Timestamp;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface OrganisationCandidate {
  key: string;
  canonicalName: string;
  aliases: string[];
  contacts: Contact[];
  industries: string[];
  locations: string[];
  sourceContactIds: string[];
}

export type SeniorityGroup = "founder-ceo" | "board" | "c-suite" | "c-1" | "partner" | "director-head" | "manager" | "other";

export const seniorityGroupLabels: Record<SeniorityGroup, string> = {
  "founder-ceo": "Founder / CEO", board: "Board", "c-suite": "C-Suite",
  "c-1": "C-1 / Senior Leadership", partner: "Partner", "director-head": "Director / Head",
  manager: "Manager", other: "Other / Unclassified",
};

export function contactSeniority(contact: Contact): SeniorityGroup {
  const title = [contact.title, contact.title2, contact.titleOthers].filter(Boolean).join(" ").toLocaleLowerCase("en");
  if (/\b(founder|co-founder|owner|chief executive|ceo|managing director)\b|(?<!vice )\bpresident\b/.test(title)) return "founder-ceo";
  if (/\b(board|chairman|chairwoman|chairperson|non-executive director|independent director)\b/.test(title)) return "board";
  if (/\bchief\b|\b(cfo|coo|cto|cio|cmo|chro|cso|cro|cdo|clo)\b/.test(title)) return "c-suite";
  if (/\b(executive vice president|senior vice president|vice president|evp|svp|vp|general manager|deputy managing director)\b/.test(title)) return "c-1";
  if (/\bpartner\b/.test(title)) return "partner";
  if (/\b(director|head of|global head|regional head|country head)\b/.test(title)) return "director-head";
  if (/\b(manager|management)\b/.test(title)) return "manager";
  return "other";
}

const text = (value: unknown): string => typeof value === "string" ? value.trim() : "";
const texts = (value: unknown): string[] => Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

export function contactFromFirestore(id: string, data: Record<string, unknown>): Contact {
  return {
    id,
    displayName: text(data.displayName) || "Unnamed contact",
    firstName: text(data.firstName),
    lastName: text(data.lastName),
    company: text(data.company),
    company2: text(data.company2),
    companyOthers: text(data.companyOthers),
    title: text(data.title),
    title2: text(data.title2),
    titleOthers: text(data.titleOthers),
    industry: text(data.industry),
    location: text(data.location),
    sources: texts(data.sources),
    emails: texts(data.emails),
    mobiles: texts(data.mobiles),
    telephones: texts(data.telephones),
    linkedinUrl: text(data.linkedinUrl),
    linkedinConnectedOn: text(data.linkedinConnectedOn),
    camcardCreateTime: text(data.camcardCreateTime),
    cardImageUrl: text(data.cardImageUrl),
    cardImageBackUrl: text(data.cardImageBackUrl),
    cardImagePath: text(data.cardImagePath),
    cardImageBackPath: text(data.cardImageBackPath),
    cardImagePaths: texts(data.cardImagePaths),
    cardImageBackPaths: texts(data.cardImageBackPaths),
    photoUrl: text(data.photoUrl),
    chineseName: text(data.chineseName),
    notes: text(data.notes),
    confidentiality: text(data.confidentiality) || "personal",
    relationshipStrength: Math.max(0, Math.min(5, Number(data.relationshipStrength) || 0)),
    followUpFlag: data.followUpFlag === true,
    followUpDate: text(data.followUpDate),
    followUpNote: text(data.followUpNote),
    lastFollowedUpAt: data.lastFollowedUpAt as Timestamp | undefined,
    createdAt: data.createdAt as Timestamp | undefined,
    updatedAt: data.updatedAt as Timestamp | undefined,
  };
}

const dateFormats: Array<{ pattern: RegExp; parse: (match: RegExpMatchArray) => Date }> = [
  { pattern: /^(\d{4})-(\d{2})-(\d{2})/, parse: (m) => new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) },
  { pattern: /^(\d{4})\/(\d{2})\/(\d{2})/, parse: (m) => new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) },
];

export function parseContactDate(value?: string): Date | null {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;
  for (const format of dateFormats) {
    const match = trimmed.match(format.pattern);
    if (match) return format.parse(match);
  }
  const parsed = new Date(trimmed.replace(/^(\d{1,2}) ([A-Za-z]{3,9}) (\d{4})$/, "$2 $1, $3"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function contactConnectedDate(contact: Contact): Date | null {
  return parseContactDate(contact.linkedinConnectedOn)
    ?? parseContactDate(contact.camcardCreateTime)
    ?? contact.createdAt?.toDate?.()
    ?? null;
}

export function organisationKey(name: string): string {
  return name.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("en");
}

export function deriveOrganisationCandidates(contacts: Contact[]): OrganisationCandidate[] {
  const groups = new Map<string, OrganisationCandidate>();
  for (const contact of contacts) {
    const names = [contact.company, contact.company2, contact.companyOthers]
      .map((name) => name.trim())
      .filter(Boolean);
    for (const name of [...new Set(names)]) {
      const key = organisationKey(name);
      if (!key) continue;
      const candidate = groups.get(key) ?? {
        key,
        canonicalName: name,
        aliases: [],
        contacts: [],
        industries: [],
        locations: [],
        sourceContactIds: [],
      };
      if (!candidate.aliases.includes(name)) candidate.aliases.push(name);
      if (!candidate.sourceContactIds.includes(contact.id)) {
        candidate.contacts.push(contact);
        candidate.sourceContactIds.push(contact.id);
      }
      if (contact.industry && !candidate.industries.includes(contact.industry)) candidate.industries.push(contact.industry);
      if (contact.location && !candidate.locations.includes(contact.location)) candidate.locations.push(contact.location);
      groups.set(key, candidate);
    }
  }
  return [...groups.values()].sort((a, b) => b.contacts.length - a.contacts.length || a.canonicalName.localeCompare(b.canonicalName));
}
